import { Box } from "@mui/material"
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react"
import useApi from "modules/arcadeGames"
import { useActiveContentContext } from "contexts/ActiveContentContext"
import { useAuthContext } from "./AuthContext"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { useGlobalAlert } from "./GlobalAlertContext"
import useSound from "hooks/useSound"
import { gamesSounds } from "utils/sounds"
import StartGame from "pages/games/components/StartGame"
import { useStudentHub } from "./StudentHubContext"
import { getScreenId } from "common/functions"
import useGameNavigation from "hooks/games/useGameNavigation"
import useGameSession from "hooks/games/useGameSession"
import useGameAnswerFlow from "hooks/games/useGameAnswerFlow"
import { routes } from "common/navigationRoutes"
import { USER_TYPES } from "common/constants"

export const GamesContext = React.createContext()

export const useGamesContext = () => {
  const context = useContext(GamesContext)
  if (context === undefined) {
    throw new Error("useGamesContext must be used within a GamesProvider")
  }
  return context
}

const GamesProvider = ({ children, sx }) => {
  const { userData, audioSettings, accountType, selectedGroupData } =
    useAuthContext()
  const [currentGame, setCurrentGame] = useState({
    colors: ["#fff", "#fff"],
  })
  const [gameSessionData, setGameSessionData] = useState(null)
  const [exerciseContentIndex, setExerciseContentIndex] = useState(0)
  const [exercisesContent, setExercisesContent] = useState([])
  const [answers, setAnswers] = useState([])
  const [isLoadingGameContent, setIsLoadingGameContent] = useState(false)
  const [isGameFinished, setIsGameFinished] = useState(false)
  const [countdownCompleted, setCountdownCompleted] = useState(false)
  const [playerStats, setPlayerStats] = useState({
    place: 0,
    totalGamesPlayed: 0,
    totalPoints: 0,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveContent } = useActiveContentContext()
  const [currentAnswerTime, setCurrentAnswerTime] = useState(0)
  const {
    setIndividualGameSession,
    setGameSessionAnswer,
    setGameSessionStatus,
    setGameSessionNextContent,
    getGameSessionById,
    getCategories,
    getGames,
    retryIndividualGameSession,
  } = useApi()
  let setSessionActivityLog = () => {}
  if (userData?.userTypeId === USER_TYPES.STUDENT) {
    setSessionActivityLog = useStudentHub().setSessionActivityLog
  }
  const { showAlert } = useGlobalAlert()
  const [playGoodAnswer] = useSound(gamesSounds.goodAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playWrongAnswer] = useSound(gamesSounds.wrongAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })

  const { fetchGameById, isPlayingGame } = useGameNavigation({
    location,
    getCategories,
    getGames,
    exercisesContent,
    exerciseContentIndex,
    setCountdownCompleted,
    setActiveContent,
  })

  const prevLocationRef = useRef(location.pathname + location.search)

  const resetGameState = useCallback(() => {
    setGameSessionData(null)
    setExercisesContent([])
    setAnswers([])
    setIsGameFinished(false)
    setExerciseContentIndex(0)
    setCountdownCompleted(false)
    setIsLoadingGameContent(false)
    setCurrentGame({ colors: ["#fff", "#fff"] })
  }, [])

  useEffect(() => {
    const prevLocation = prevLocationRef.current
    const currentLocation = location.pathname + location.search

    if (prevLocation !== currentLocation) {
      const prevPath = prevLocation.toLowerCase()
      const currentPath = location.pathname.toLowerCase()

      const wasInGame = prevPath.includes("/arcadeGame/")
      const isGamesList =
        currentPath.endsWith(routes.NEW_GAMES.toLowerCase()) ||
        currentPath.endsWith(routes.NEW_GAMES.toLowerCase() + "/")
      const isGameRoute =
        currentPath.includes(routes.NEW_GAMES.toLowerCase()) && !isGamesList

      if ((wasInGame && !isGameRoute) || isGamesList) {
        resetGameState()
      }
    }

    prevLocationRef.current = location.pathname + location.search
  }, [location.pathname, location.search])

  const setNextContent = useCallback(async () => {
    const nextIndex = exerciseContentIndex + 1
    if (nextIndex < exercisesContent.length) {
      setExerciseContentIndex((prevIndex) => prevIndex + 1)
    } else {
      const { gameSessionId, participants } = gameSessionData
      const participantId =
        participants?.find((p) => p.userId === userData?.userId)
          ?.gameSessionParticipantId || -1
      await setSessionActivityLog({
        gameSessionContentId: 0,
        viewId: getScreenId("games"),
      })
      await setGameSessionStatus({
        gameSessionId,
        gameSessionParticipantId: participantId,
        gameSessionStatusId: 4,
      })
      setTimeout(() => {
        setIsGameFinished(true)
      }, 1000)
    }
    setIsLoadingGameContent(false)
  }, [exerciseContentIndex, exercisesContent, gameSessionData, userData])

  const finishGameSession = useCallback(async () => {
    const { gameSessionId, participants } = gameSessionData
    const participantId =
      participants?.find((p) => p.userId === userData?.userId)
        ?.gameSessionParticipantId || -1
    await setSessionActivityLog({
      gameSessionContentId: 0,
      viewId: getScreenId("games"),
    })
    await setGameSessionStatus({
      gameSessionId,
      gameSessionParticipantId: participantId,
      gameSessionStatusId: 4,
    })
    setIsGameFinished(true)
  }, [gameSessionData])

  const { formatOnlyAnswered, formatPlayerAnswers, sendAnswer } =
    useGameAnswerFlow({
      gameSessionData,
      currentGame,
      exercisesContent,
      exerciseContentIndex,
      setAnswers,
      setGameSessionData,
      setIsLoadingGameContent,
      setIsGameFinished,
      setNextContent,
      finishGameSession,
      setGameSessionAnswer,
      setGameSessionStatus,
      setGameSessionNextContent,
      userData,
      currentAnswerTime,
      playGoodAnswer,
      playWrongAnswer,
    })

  const { loadSession, createGame, loadGameContent, loadNewGameContent } =
    useGameSession({
      gameSessionData,
      setGameSessionData,
      exercisesContent,
      exerciseContentIndex,
      setExercisesContent,
      setExerciseContentIndex,
      setIsGameFinished,
      setAnswers,
      setCountdownCompleted,
      setIsLoadingGameContent,
      setCurrentGame,
      formatOnlyAnswered,
      fetchGameById,
      getGameSessionById,
      setIndividualGameSession,
      retryIndividualGameSession,
      userData,
      selectedGroupData,
      navigate,
      showAlert,
      setSearchParams,
      searchParams,
      location,
    })

  const gameAnswers = useMemo(() => {
    if (isGameFinished) {
      return formatPlayerAnswers(answers, exercisesContent)
    }
    return answers
  }, [isGameFinished, answers, exercisesContent, formatPlayerAnswers])

  const value = useMemo(
    () => ({
      game: currentGame,
      level: {},
      exercisesContent,
      gameSessionData,
      gameAnswers,
      exerciseContentIndex,
      isLoadingGameContent,
      isGameFinished,
      gameTime: accountType < 7 ? 30 : 20,
      countdownCompleted,
      playerStats,
      setGame: setCurrentGame,
      createGame,
      sendAnswer,
      loadGameSession: loadSession,
      setNextContent,
      loadGameContent,
      setCountdownCompleted,
      setPlayerStats,
      setIsLoadingGameContent,
      setCurrentAnswerTime,
      resetGameState,
      loadNewGameContent,
      finishGameSession,
    }),
    [
      currentGame,
      exercisesContent,
      gameSessionData,
      gameAnswers,
      userData?.userId,
      exerciseContentIndex,
      isLoadingGameContent,
      searchParams,
      isGameFinished,
      countdownCompleted,
      selectedGroupData?.groupId,
      playerStats,
      currentAnswerTime,
    ]
  )

  return (
    <GamesContext.Provider value={value}>
      <Box sx={sx} className={"games-context"}>
        <Box key={location.pathname + location.search}>{children}</Box>
        {isPlayingGame && !countdownCompleted && currentGame && (
          <StartGame
            game={currentGame}
            onComplete={() => setCountdownCompleted(true)}
          />
        )}
      </Box>
    </GamesContext.Provider>
  )
}

export default GamesProvider
